import type { ErrorEvent } from '@sentry/react-native';
import { isTransientOpsNetworkError } from './expectedErrors';

type SentryBeforeSendHint = {
  originalException?: unknown;
};

function exceptionText(event: ErrorEvent, hint: SentryBeforeSendHint): string {
  const fromHint =
    hint.originalException instanceof Error
      ? `${hint.originalException.name} ${hint.originalException.message}`
      : '';
  const fromEvent =
    event.exception?.values?.map((v) => `${v.type ?? ''} ${v.value ?? ''}`).join(' ') ?? '';
  // PostgrestError/AuthError no son instanceof Error: el SDK los captura como
  // "Object captured as exception" y el mensaje real queda en extra.__serialized__
  // (VUELTO-NEGOCIO-3J: UnknownHostException no llegaba al filtro de red).
  const serialized = (event.extra as Record<string, unknown> | undefined)?.__serialized__;
  const fromSerialized =
    serialized && typeof serialized === 'object'
      ? Object.values(serialized as Record<string, unknown>)
          .filter((v): v is string => typeof v === 'string')
          .join(' ')
      : '';
  return `${fromHint} ${fromEvent} ${fromSerialized}`.toLowerCase();
}

function isUserVisibleEvent(event: ErrorEvent): boolean {
  const tags = event.tags as Record<string, unknown> | undefined;
  if (!tags || typeof tags !== 'object') return false;
  return String(tags.user_visible) === 'true';
}

/** Drop known noisy / expected web storage errors from Sentry issue stream. */
export function sentryBeforeSend(event: ErrorEvent, hint: SentryBeforeSendHint): ErrorEvent | null {
  const text = exceptionText(event, hint);
  const userVisible = isUserVisibleEvent(event);

  if (text.includes('databaseunavailableerror') || text.includes('invalid vfs state')) {
    return null;
  }

  // OPFS InvalidStateError (Safari iOS): expo-sqlite web worker lanza un
  // DOMException name=InvalidStateError message="The object is in an invalid
  // state." cuando OPFS no está disponible (modo privado) o el access handle
  // queda en estado inválido. La app ya degrada a DatabaseUnavailableError;
  // este rechazo crudo se cuela por los filtros de arriba.
  if (text.includes('invalidstateerror') && text.includes('the object is in an invalid state')) {
    return null;
  }

  if (text.includes('asyncrequireerror') && text.includes('flushregistry')) {
    return null;
  }

  // Chunk async post-deploy: cliente viejo pide hash que ya no existe.
  // Sale path ya es eager; esto tapa ruido residual / otros lazy adapters.
  if (text.includes('asyncrequireerror') && text.includes('loading module')) {
    return null;
  }

  // Auth rate limit (429): UX esperada, no bug.
  if (text.includes('supabase auth rate limit')) {
    return null;
  }

  // JWT expired/invalid: el auth retry (supabaseFetchWithAuthRetry) ya intentó
  // refrescar y el auth layer maneja logout. Reportar ruido de sesión muerta
  // desde best-effort queries (badges, counts) no es bug. user_visible se preserva.
  if (!userVisible && (text.includes('jwt expired') || text.includes('invalid jwt'))) {
    return null;
  }

  // catalog load invalidated: rechazo esperado al invalidar el cache del
  // catálogo POS (cambio de filtro/sucursal mientras un load estaba en vuelo).
  // El hook usePosCatalogPage ya maneja el error silenciosamente.
  if (text.includes('catalog load invalidated')) {
    return null;
  }

  // removeChild NotFoundError (RN-web): SentryErrorBoundary ya reintenta en
  // silencio (hasta 3x), pero el SDK auto-captura cada catch en componentDidCatch
  // antes del guard de onError. Solo reporta el path manual cuando la recovery
  // se agotó y el usuario vio "Algo se rompió" (tag domRecovery=exhausted).
  // VUELTO-NEGOCIO-2W/2X/3A.
  const tags = event.tags as Record<string, unknown> | undefined;
  if (
    text.includes('notfounderror') &&
    text.includes('removechild') &&
    String(tags?.domRecovery) !== 'exhausted'
  ) {
    return null;
  }

  // Network errors transitorios: el sync coordinator reintenta automáticamente.
  // Excepción: user_visible (picker CSP, etc.) a menudo llega como Failed to fetch.
  if (!userVisible && isTransientOpsNetworkError(text)) {
    return null;
  }

  return event;
}
